import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/app/common/common-components/ui/Dialog";
import { Button } from "@/app/common/common-components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/common/common-components/ui/Table";
import { MessageTemplate } from "@/app/models/MessageTemplateModel";
import { useQuery } from "@tanstack/react-query";
import apiAgent from "@/app/api/apiAgent";
import NewLoader from "@/app/layout/NewLoader";
import { ExternalLink } from "lucide-react";
import { Link } from "@/app/common/common-components/ui/Link";
import { useStore } from "@/app/stores/store";

interface TemplateSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  onTemplateSelect: (template: MessageTemplate) => void;
  filterType?: "Email" | "SMS" | "Whatsapp";
}

const TemplateSelectionDialog: React.FC<TemplateSelectionDialogProps> = ({
  open,
  onOpenChange,
  userId,
  onTemplateSelect,
  filterType,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const {
    userStore: { userSession },
  } = useStore();

  useEffect(() => {
    if (open) {
      setSelectedTemplateId(null);
    }
  }, [open]);

  const { data: messageTemplates, status } = useQuery({
    queryKey: ["messageTemplates", userId],
    queryFn: () => apiAgent.MessageTemplate.getMessageTemplates(userId),
    enabled: open,
  });

  const selectedTemplate =
    messageTemplates?.find((t) => t.templateId === selectedTemplateId) || null;

  const handleTemplateSelect = () => {
    if (selectedTemplate) {
      onTemplateSelect(selectedTemplate);
      onOpenChange(false);
    }
  };

  if (status === "pending") {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:tw-max-w-4xl">
          <DialogHeader>
            <DialogTitle>Select Template</DialogTitle>
          </DialogHeader>
          <div className="tw-flex tw-justify-center tw-py-8">
            <NewLoader text="Loading templates..." iconOnly />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-h-[80vh] tw-overflow-y-auto sm:tw-max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select Template</DialogTitle>
        </DialogHeader>
        <div className="tw-grid tw-grid-cols-2 tw-gap-6">
          <div className="tw-flex-1 tw-space-y-4">
            {messageTemplates?.length === 0 ? (
              <div className="tw-py-8 tw-text-center tw-text-muted-foreground">
                No templates available
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Subject</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messageTemplates
                    ?.filter((template) => {
                      if (filterType) {
                        return template.templateType === filterType;
                      }
                      return true;
                    })
                    .map((template) => (
                      <TableRow
                        key={template.templateId}
                        className={
                          selectedTemplateId === template.templateId
                            ? "tw-bg-muted/50 tw-cursor-pointer"
                            : "tw-cursor-pointer"
                        }
                        onClick={() =>
                          setSelectedTemplateId(template.templateId)
                        }
                      >
                        <TableCell className="tw-space-x-2 tw-font-medium">
                          <input
                            type="radio"
                            name="template"
                            checked={selectedTemplateId === template.templateId}
                            onChange={() =>
                              setSelectedTemplateId(template.templateId)
                            }
                          />
                          <span>{template.templateName}</span>
                        </TableCell>
                        <TableCell className="tw-max-w-xs tw-truncate">
                          {template.subject || "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </div>
          <div className="tw-border-l tw-border-border tw-pl-6">
            <div className="tw-sticky tw-top-0">
              {selectedTemplate ? (
                <div className="tw-space-y-4">
                  {selectedTemplate.templateType === "Email" &&
                    selectedTemplate.subject && (
                      <div>
                        <h4 className="tw-text-sm tw-font-medium tw-text-muted-foreground">
                          Subject
                        </h4>
                        <p className="tw-text-sm">{selectedTemplate.subject}</p>
                      </div>
                    )}
                  <div>
                    {selectedTemplate.templateType === "Email" ? (
                      <div
                        className="tw-bg-muted/50 tw-max-h-64 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-border tw-p-3 tw-text-sm"
                        dangerouslySetInnerHTML={{
                          __html: selectedTemplate.content,
                        }}
                      />
                    ) : (
                      <div className="tw-bg-muted/50 tw-max-h-64 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-border tw-p-3 tw-text-sm">
                        {selectedTemplate.content}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-muted-foreground">
                  <p className="tw-text-sm">Select a template to preview</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-4">
          <Link
            to={`/Settings/SaveUser/${userSession?.userId}`}
            className="tw-flex tw-items-center tw-gap-2"
            state={{ tab: "messagetemplates" }}
          >
            Configure templates
            <ExternalLink className="tw-h-4 tw-w-4" />
          </Link>
          <Button onClick={handleTemplateSelect} disabled={!selectedTemplate}>
            Use this template
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateSelectionDialog;
